import { useState, useEffect } from 'react';
import { FetchRequestTypes, useFetch } from '../../../hooks/useFetch';
import {
  ArrivalDepartureTeaserData,
  ArrivalsData,
  ArrivalsDataItem,
  DeparturesData,
  DeparturesDataItem,
} from '../interfaces/arrivalsDepartures.interfaces';
import dayjs, { Dayjs } from 'dayjs';

export interface ClosestArrivalItem {
  item: ArrivalsDataItem | null;
  ms: string | null;
}

export interface ClosestDepartureItem {
  item: DeparturesDataItem | null;
  ms: string | null;
}

export const useArrivalDepartureTeaserData = (refresh: boolean) => {
  const [dataUrls, setDataUrls] = useState<string[] | null>(null);
  const [arrivalDepartureData, setArrivalDepartureData] = useState<ArrivalDepartureTeaserData | null>(null);

  const [nearestArrivalItem, setNearestArrivalItem] = useState<ArrivalsDataItem | null>(null);
  const [nearestDepartureItem, setNearestDepartureItem] = useState<DeparturesDataItem | null>(null);

  const { data: dataLoaded, loading, error } = useFetch(dataUrls, FetchRequestTypes.GET);
  const excludedImoNumbers = [
    '9864277',
    '9131199',
    '9200158',
    '9333917',
    '9390458',
    '9236315',
    '9346641',
    '8327105',
    '9631436',
    '9327475',
  ];

  const itemsToLoad = 60;

  useEffect(() => {
    if (refresh) {
      setArrivalDepartureData(null);

      const today = dayjs().toISOString();
      setDataUrls([
        `/api-aad/web-backend/v1/arrival?sortOrder=ASC&from=${today}&count=${itemsToLoad}`,
        `/api-aad/web-backend/v1/departure?sortOrder=ASC&from=${today}&count=${itemsToLoad}`,
      ]);
    }
  }, [refresh]);

  useEffect(() => {
    if (dataLoaded) {
      const today = dayjs();

      const expectedArrivals = (dataLoaded[0] as ArrivalsData).items
        .filter((item) => !item.isActual)
        .filter((item) => today.isBefore(dayjs(item.arrivalTime).format()))
        .filter((item) => item.name.toLowerCase() !== 'portcontrol' && item.name.toLowerCase() !== 'port control')
        .filter((item) => !excludedImoNumbers.some((exclImo) => item.imo === exclImo));

      const expectedDepartures = (dataLoaded[1] as DeparturesData).items
        .filter((item) => !item.isActual)
        .filter((item) => today.isBefore(item.departureTime))
        .filter((item) => item.name.toLowerCase() !== 'portcontrol' && item.name.toLowerCase() !== 'port control')
        .filter((item) => !excludedImoNumbers.some((exclImo) => item.imo === exclImo));

      let arrivalDate: ArrivalsDataItem | null = null;
      let departureDate: DeparturesDataItem | null = null;

      let arrivalCount = 0;
      expectedArrivals.forEach((item) => {
        const diff = dayjs(item.arrivalTime).diff(today);
        if (diff > 0) {
          if (arrivalDate?.arrivalTime) {
            if (dayjs(item.arrivalTime).diff(arrivalDate.arrivalTime) < 0) {
              arrivalDate = item;
            }
          } else {
            arrivalDate = item;
          }
          arrivalCount++;

          if (arrivalCount === expectedArrivals.length) {
            setNearestArrivalItem(arrivalDate);
          }
        }
      });

      let departureCount = 0;
      expectedDepartures.forEach((item) => {
        const diff = dayjs(item.departureTime).diff(today);
        if (diff > 0) {
          if (departureDate?.departureTime) {
            if (dayjs(item.departureTime).diff(departureDate.departureTime) < 0) {
              departureDate = item;
            }
          } else {
            departureDate = item;
          }

          departureCount++;
          if (departureCount === expectedDepartures.length) {
            setNearestDepartureItem(departureDate);
          }
        }
      });

      setDataUrls(null);
    }
  }, [dataLoaded]);

  useEffect(() => {
    if (nearestArrivalItem && nearestDepartureItem) {
      setArrivalDepartureData({ arrival: nearestArrivalItem, departure: nearestDepartureItem });
    }
  }, [nearestArrivalItem, nearestDepartureItem]);

  return {
    arrivalDepartureData,
  };
};

